import React, { useEffect, useRef } from "react";
import Image from "next/image";
import Link from "next/link";

const Modal = ({ isOpen, onClose }) => {
  const modalRef = useRef(null);

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      // Prevent scrolling when modal is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "auto";
    };
  }, [isOpen, onClose]);

  // Close on escape key press
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className={`fixed-modal ${isOpen ? "block" : "hidden"}`}
      onClick={onClose}
    >
      <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm mt-[60px] md:mt-0">
        <div
          ref={modalRef}
          className="relative bg-white/60 dark:bg-jacarta-900/60 rounded-lg shadow-xl max-w-4xl w-full max-h-[calc(100vh-80px)] md:max-h-[90vh] overflow-auto"
          aria-modal="true"
          role="dialog"
        >
          {/* Add close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 md:hidden z-50"
            aria-label="Close modal"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.93073 15.5807L0.414062 14.0641L6.48073 7.9974L0.414062 1.93073L1.93073 0.414062L7.9974 6.48073L14.0641 0.414062L15.5807 1.93073L9.51406 7.9974L15.5807 14.0641L14.0641 15.5807L7.9974 9.51406L1.93073 15.5807Z"
                fill="white"
              />
            </svg>
          </button>

          {/* Modal content */}
          <div className="p-5 md:py-20 bg-jacarta-900/80">
            <h2 className="text-4xl md:text-5xl font-display text-white mb-8">
              Using Tech for <span className="text-accent">Good</span>
            </h2>
            <div className="flex flex-col justify-between md:flex-row items-center px-8">
              {/* Text content */}
              <div className="md:w-1/2 text-left">
                <p className="text-md leading-9 text-white/90 mb-4">
                  At the intersection of people, planet, profit, and purpose,
                  Impactoverse is an ecosystem leveraging technology and
                  creativity for real-world positive outcomes. Through engaging,
                  interactive, and immersive technologies, we are transforming
                  the ways in which we raise awareness of SDG-aligned activity
                  and enable actions to address pressing environmental and
                  social issues
                  <Link
                    href="/tech-for-good"
                    className="text-accent hover:text-accent-dark text-sm font-semibold transition-colors ml-2"
                    onClick={onClose}
                  >
                    READ MORE
                  </Link>
                </p>
              </div>

              {/* Image */}
              <div className="md:w-1/2">
                <div className="overflow-hidden">
                  <Image
                    src="/images/earth-tech.jpg"
                    alt="Earth technology visualization"
                    width={500}
                    height={700}
                    className="object-cover w-full h-[350px]"
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    priority
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Modal;
